// Resolving which registered business location a capsule sits on, and what to
// call that location on screen. Pure — no Firestore, no window, no Date — so the
// gem list and the map markers can share one source of truth.

// "At the exact location" means exactly that: the capsule is on the location's
// own point. Comparing at six decimals (~11 cm) absorbs serialization noise
// without admitting any distance a person could perceive. It matches the
// precision buildLocationClusterKey already uses.
const COORDINATE_DECIMALS = 6;

export function normalizeLabelText(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
}

function roundCoordinate(value) {
  const next = toFiniteNumber(value);
  return next === null ? null : Number(next.toFixed(COORDINATE_DECIMALS));
}

export function getUsableCoordinates(source = {}) {
  const nested = source?.location || {};
  const lat = roundCoordinate(
    nested?.lat ?? nested?.latitude ?? source?.lat ?? source?.latitude
  );
  const lng = roundCoordinate(
    nested?.lng ??
      nested?.lon ??
      nested?.longitude ??
      source?.lng ??
      source?.lon ??
      source?.longitude
  );
  if (lat === null || lng === null) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
}

export function resolveBusinessLocationForCapsule(capsule = {}, businessLocations = []) {
  const locations = Array.isArray(businessLocations) ? businessLocations.filter(Boolean) : [];
  if (!locations.length) return null;

  const capsulePoint = getUsableCoordinates(capsule);
  if (capsulePoint) {
    const exact = locations.find((location) => {
      const point = getUsableCoordinates(location);
      return Boolean(point) && point.lat === capsulePoint.lat && point.lng === capsulePoint.lng;
    });
    if (exact) return exact;
  }

  // Coordinates are authoritative. When the capsule has a point, only locations
  // that have none stay eligible for the weaker signals below — a placeId or
  // address match must never rescue a capsule that sits somewhere else.
  const candidates = capsulePoint
    ? locations.filter((location) => !getUsableCoordinates(location))
    : locations;
  if (!candidates.length) return null;

  const capsuleLocationId = normalizeLabelText(
    capsule?.locationId || capsule?.location?.locationId
  );
  if (capsuleLocationId) {
    const match = candidates.find(
      (location) => normalizeLabelText(location?.id) === capsuleLocationId
    );
    if (match) return match;
  }

  const capsulePlaceId = normalizeLabelText(capsule?.location?.placeId || capsule?.placeId);
  if (capsulePlaceId) {
    const match = candidates.find(
      (location) => normalizeLabelText(location?.placeId) === capsulePlaceId
    );
    if (match) return match;
  }

  const capsuleAddress = normalizeLabelText(
    capsule?.location?.address ||
      capsule?.address ||
      capsule?.formattedAddress ||
      capsule?.formatted_address
  );
  if (capsuleAddress) {
    // Legacy business documents store the location's name in the address slot,
    // so both sides have to be tried.
    const match = candidates.find(
      (location) =>
        normalizeLabelText(location?.address) === capsuleAddress ||
        normalizeLabelText(location?.name) === capsuleAddress
    );
    if (match) return match;
  }

  return null;
}

const LABEL_SEPARATOR = " · ";
const DEFAULT_TITLE = "Time capsule";
const DEFAULT_SECONDARY = "Recommended nearby";

export function resolveBusinessLocationLabel(location = {}, { companyName = "" } = {}) {
  const name = String(location?.name || "").trim();
  const address = String(location?.address || "").trim();
  const company = String(companyName || "").trim();
  // A "real" name is one the owner chose, not the address echoed into the label
  // slot — which is what Studio's legacy mirror and reconstructed entries do.
  const hasRealName = Boolean(name) && normalizeLabelText(name) !== normalizeLabelText(address);

  if (location?.isHeadquarters === true) {
    return hasRealName ? name : company || name || address;
  }
  return hasRealName ? name : address;
}

// Studio fills a blank subject from placeName → businessName → address at bury
// time, so a capsule's persisted title is frequently not a message title at all.
export function isPlaceholderValue(value, placeholders = []) {
  const candidate = normalizeLabelText(value);
  if (!candidate) return true;
  return (Array.isArray(placeholders) ? placeholders : [])
    .map(normalizeLabelText)
    .filter(Boolean)
    .includes(candidate);
}

export function isPlaceholderTitle(
  value,
  { capsule = {}, locationLabel = "", companyName = "" } = {}
) {
  return isPlaceholderValue(value, [
    capsule?.location?.address,
    capsule?.address,
    capsule?.location?.placeName,
    capsule?.placeName,
    locationLabel,
    companyName,
  ]);
}

export function buildBusinessCapsuleLabels({
  capsule = {},
  locationLabel = "",
  companyName = "",
} = {}) {
  const label = String(locationLabel || "").trim();
  const company = String(companyName || "").trim();
  const context = { capsule, locationLabel: label, companyName: company };

  const title =
    [capsule?.messageTitle, capsule?.title, capsule?.headline]
      .map((value) => String(value || "").trim())
      .find((value) => value && !isPlaceholderTitle(value, context)) ||
    String(capsule?.seedName || "").trim() ||
    label ||
    String(capsule?.messageBody || "").trim() ||
    DEFAULT_TITLE;

  const businessPart = company || String(capsule?.businessName || "").trim();
  const locationPart =
    label ||
    String(capsule?.locationName || "").trim() ||
    String(capsule?.location?.address || capsule?.address || "").trim();

  const parts = [];
  if (businessPart) parts.push(businessPart);
  if (
    locationPart &&
    normalizeLabelText(locationPart) !== normalizeLabelText(businessPart) &&
    normalizeLabelText(locationPart) !== normalizeLabelText(title)
  ) {
    parts.push(locationPart);
  }

  return { title, secondary: parts.join(LABEL_SEPARATOR) || DEFAULT_SECONDARY };
}

const BUSINESS_GEM_KINDS = new Set(["sponsored", "memberExclusive"]);

// Applied by the fetchNearbyGems hydration pass, where the business documents are
// in hand. Non-business gems and gems whose business wasn't fetched pass through
// by reference, untouched.
export function applyBusinessGemLabels({ gems = [], businessMarkerMap = {} } = {}) {
  return (Array.isArray(gems) ? gems : []).map((gem) => {
    if (!BUSINESS_GEM_KINDS.has(gem?.gemKind)) return gem;

    const businessId = String(gem?.raw?.businessId || gem?.raw?.senderId || "").trim();
    const metadata = businessMarkerMap?.[businessId];
    if (!metadata) return gem;

    const capsule = gem?.raw || {};
    const matched = resolveBusinessLocationForCapsule(capsule, metadata.locations);
    const locationLabel = matched
      ? resolveBusinessLocationLabel(matched, { companyName: metadata.companyName })
      : "";
    return {
      ...gem,
      companyName: metadata.companyName || "",
      locationLabel: locationLabel || gem.locationLabel || "",
      placeLabel: locationLabel || gem.placeLabel || "",
      businessAvatarUrl: gem.businessAvatarUrl || metadata.avatarUrl || "",
    };
  });
}
